import React, { useState } from 'react';
import { PenTool, MessageCircle, BookText, Check, ChevronDown, ChevronUp, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FirstSteps - Carte "Premiers pas" persistante pour les nouveaux utilisateurs.
 * Remplace le carrousel Onboarding passif par une approche guidée et actionnable.
 * 
 * Props:
 * - stepsCompleted: { step1: boolean, step2: boolean, step3: boolean }
 * - onStartCalligraphy: () => void  - Ouvre la pratique calligraphie sur une consonne de départ
 * - onOpenManee: () => void         - Ouvre le chat Manee
 * - onOpenStory: () => void         - Ouvre le générateur d'histoires
 * - allDone: boolean                - true si les 3 pas sont complétés
 * - onDismiss: () => void           - Retire la carte après "Bravo"
 */
const FirstSteps = ({ 
  stepsCompleted, 
  onStartCalligraphy, 
  onOpenManee, 
  onOpenStory,
  allDone,
  onDismiss
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const steps = [
    {
      id: 1,
      key: 'step1',
      icon: PenTool,
      title: 'Écrire ta première lettre',
      description: 'Trace une consonne thaïe en calligraphie.',
      color: 'amber',
      action: onStartCalligraphy,
      actionLabel: 'Commence ici'
    },
    {
      id: 2,
      key: 'step2',
      icon: MessageCircle,
      title: 'Parler à Manee',
      description: 'Pose une question à ton assistante thaïe.',
      color: 'pink',
      action: onOpenManee,
      actionLabel: 'Ouvrir le chat'
    },
    {
      id: 3,
      key: 'step3',
      icon: BookText,
      title: 'Lire une histoire',
      description: 'Génère une micro-histoire pour pratiquer la lecture.',
      color: 'fuchsia',
      action: onOpenStory,
      actionLabel: 'Générer une histoire'
    }
  ];

  const completedCount = Object.values(stepsCompleted).filter(Boolean).length;

  // État "Bravo" : tous les pas faits
  if (allDone) {
    return (
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-200/50 mb-6" data-testid="first-steps-complete">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
              <PartyPopper className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-emerald-800">
                Bravo, tu as fait le tour !
              </h3>
              <p className="text-sm text-emerald-600">
                Tu connais maintenant les bases de ThaiPractice.
              </p>
            </div>
          </div>
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
            data-testid="first-steps-dismiss"
          >
            Continuer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl border border-violet-200/50 mb-6 overflow-hidden" data-testid="first-steps-card">
      {/* Header - toujours visible */}
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-violet-100/30 transition-colors"
        data-testid="first-steps-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <PenTool className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-base text-violet-800">Premiers pas</h3>
            <p className="text-xs text-violet-600">{completedCount}/3 étapes complétées</p>
          </div>
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-5 h-5 text-violet-400" />
        ) : (
          <ChevronUp className="w-5 h-5 text-violet-400" />
        )}
      </button>

      {/* Contenu dépliable */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-3" data-testid="first-steps-content">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isDone = stepsCompleted[step.key];
            const isFirst = index === 0 && !isDone;

            return (
              <div 
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isDone 
                    ? 'bg-white/50 opacity-70' 
                    : isFirst 
                      ? 'bg-white shadow-sm ring-2 ring-amber-300' 
                      : 'bg-white/70'
                }`}
                data-testid={`first-step-${step.id}`}
              >
                {/* Icône avec état */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isDone 
                    ? 'bg-emerald-100' 
                    : `bg-${step.color}-100`
                }`}>
                  {isDone ? (
                    <Check className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <Icon className={`w-5 h-5 text-${step.color}-600`} />
                  )}
                </div>

                {/* Texte */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDone ? 'text-slate-500 line-through' : 'text-slate-700'}`}>
                    {step.title}
                  </p>
                  {!isDone && (
                    <p className="text-xs text-slate-500 truncate">{step.description}</p>
                  )}
                </div>

                {/* Bouton action */}
                {!isDone && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      step.action();
                    }}
                    size="sm"
                    className={`flex-shrink-0 rounded-lg text-xs ${
                      isFirst 
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                    }`}
                    data-testid={`first-step-${step.id}-action`}
                  >
                    {isFirst ? step.actionLabel : 'Faire'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FirstSteps;
